import { useCallback } from "react";
import React, { useEffect, useState } from "react";
import { CreditCard, Plus, Landmark, Trash2 } from "lucide-react";
import { Spinner } from "react-bootstrap";
import { OrgPaymentMethodDto, SubscriptionDto } from "../../../../interfaces";
import { PaymentMethodType, SubscriptionStatus } from "../../../../Data/SD";
import { useAbortManager } from "../../../../hooks/cancelletionManagerHook/useAbortManager";
import { useAuth } from "../../../../firebase/AuthProvider";
import { getMyOrganizationSubscriptionAsync } from "../../../../services/PaymentServices/OrgPaymentMethodService";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import PaymentMethodAdder from "./PaymentMethodAdder";
import { RemovePaymentMethodAsync } from "../../../../services/PaymentServices/HelimCustomerService";
import { showConfirmationDialog } from "../../../modal/ConfimationModal/Confimation";
interface PaymentMethodsProps {
    subscription: SubscriptionDto,
    isSuperAdmin: boolean | undefined,
    orgId? : number | undefined
}

const PaymentMethods: React.FC<PaymentMethodsProps> = ({
    subscription,
    isSuperAdmin = false,
    orgId
}) => {
    const cancelletionManager = useAbortManager();
    const { currentUser } = useAuth();

    // Data
    const [paymentMethods, setPaymentMethods] = useState<OrgPaymentMethodDto[]>([]);

    const [reload, setReload] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(true);
    const [removingId, setRemovingId] = useState<string | number | null>(null);

    const getAllPaymentMethods = useCallback(async () => {
        if (!currentUser) return;

        const token = await currentUser.getIdToken();

        try {
            const controller = cancelletionManager.create("GetPaymentMethod");

            const paymentRes = await getMyOrganizationSubscriptionAsync(
                token,
                orgId,
                controller.signal
            );

            if (paymentRes.success) {
                setPaymentMethods(paymentRes.data);
            }

            setLoading(false);

        } catch (error: any) {
            if (error.name === 'AbortError') return;
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to get the data");
        } finally {
            cancelletionManager.remove("GetPaymentMethod");
        }
    }, [currentUser, orgId, cancelletionManager]);

useEffect(() => {
        getAllPaymentMethods();
    }, [reload, getAllPaymentMethods]);



    const handleRemovePaymentMethod = async (paymentMethodId: string | number) => {
        if (!currentUser) return;

        const confirm = await showConfirmationDialog(
            "Remove Payment Method",
            "Are you sure you want to remove this payment method?",
            "Yes, remove it!",
            "Cancel"
        );

        if (!confirm) return;

        setRemovingId(paymentMethodId);

        try {
            const token = await currentUser.getIdToken();
            const res = await RemovePaymentMethodAsync(token, paymentMethodId.toString());

            if (res.success) {
                toast.success("Payment method removed successfully");
                setReload(!reload);
            } else {
                toast.error(res.message || "Failed to remove payment method");
            }
        } catch (error: any) {
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error removing payment method");
        } finally {
            setRemovingId(null);
        }
    };

    const startSubscriptionBtnEnable = subscription?.status === SubscriptionStatus.TRIAL_ACTIVE.toString() || subscription?.status === SubscriptionStatus.READ_ONLY_TRIAL_EXPIRED.toString();

    const hasCard = paymentMethods.some(
        p => p.paymentType === PaymentMethodType.CARD.toString()
    );

    const hasACH = paymentMethods.some(
        p => p.paymentType === PaymentMethodType.ACH.toString()
    );
    return (
        <div className="pm_container">
            <h3 className="pm_title">Payment Methods</h3>

            <>
                {
                    startSubscriptionBtnEnable
                        ? (
                            <>
                                <span className="d-flex justify-content-center align-items-center m-5">
                                    <button>Start Subscription</button>
                                </span>
                            </>
                        )
                        : (
                            <div>
                                {loading ? (
                                    <div className="d-flex justify-content-center align-items-center">
                                        <Spinner size="sm" /> Loading Payment methods...
                                    </div>
                                )
                                    : (
                                        paymentMethods.map((pm) => {
                                            const isCard = pm.paymentType === PaymentMethodType.CARD;
                                            return (
                                                <div className="pm_card" key={pm.id}>
                                                    <div className="pm_card_icon">
                                                        {isCard ? <CreditCard size={20} /> : <Landmark size={20} />}
                                                    </div>

                                                    <div className="pm_card_info">
                                                        <p className="pm_card_title">
                                                            {isCard ? "CARD" : "BANK"} ending in {pm.last4}
                                                        </p>

                                                        {/* <span className="pm_card_sub">
                                                        Expires {pm.expMonth}/{pm.expYear}
                                                    </span> */}
                                                    </div>

                                                    {pm.isDefault ? (
                                                        <span className="pm_default">Default</span>
                                                    ) : (
                                                        <button
                                                            className="btn btn-sm text-danger pm_remove"
                                                            onClick={() => handleRemovePaymentMethod(pm.id)}
                                                            title="Remove payment method"
                                                            disabled={removingId === pm.id}
                                                        >
                                                            {removingId === pm.id ? <Spinner size="sm" /> : <Trash2 size={20} />}
                                                        </button>
                                                    )}
                                                </div>
                                            )
                                        })
                                    )
                                }
                                <div className="pm_auto">
                                    ✓ Auto-charge enabled
                                </div>

                                {
                                    !isSuperAdmin && !loading && (
                                        <div className="pm_actions">
                                            {!hasCard && (
                                                <PaymentMethodAdder
                                                    buttonText="Add New Card"
                                                    icon={<Plus size={16} />}
                                                    methodType="cc"
                                                    reload={() => setReload(!reload)}
                                                />
                                            )}

                                            {!hasACH && (
                                                <PaymentMethodAdder
                                                    buttonText="Add Bank (ACH)"
                                                    icon={<Landmark size={16} />}
                                                    methodType="ach"
                                                    reload={() => setReload(!reload)}
                                                />
                                            )}
                                        </div>
                                    )
                                }
                            </div>
                        )
                }
            </>

        </div>
    );
};

export default PaymentMethods;